// Full-screen "whisk away" overlay used when the hero travels to the Capital and back.
// An ink-black sheet wipes in across the screen (with speed streaks), shows the
// destination + a real loading bar, then wipes away to reveal the new place.
// Promise-based so capitalScene can sequence: open → load/swap → close.

import gsap from 'gsap'

let el = null
let bar = null
let titleEl = null
let cnEl = null
let tipEl = null
let busy = false

const TIPS = [
  'The Capital never sleeps — and neither do its tax collectors.',
  'Mind the lanterns. Some of them are watching.',
  'Great fortunes and great threats both live behind these walls.',
  'Keep to the main road, little Mao.',
]

function ensure() {
  if (el) return
  el = document.createElement('div')
  el.className = 'capital-travel'
  el.setAttribute('role', 'status')
  el.setAttribute('aria-live', 'polite')
  el.innerHTML = `
    <div class="capital-travel__streaks"></div>
    <div class="capital-travel__seal" data-ct-cn>京城</div>
    <div class="capital-travel__card">
      <div class="capital-travel__title" data-ct-title>Travelling…</div>
      <div class="capital-travel__bar"><div class="capital-travel__fill" data-ct-fill></div></div>
      <div class="capital-travel__tip" data-ct-tip></div>
    </div>
  `
  document.body.appendChild(el)
  bar = el.querySelector('[data-ct-fill]')
  titleEl = el.querySelector('[data-ct-title]')
  cnEl = el.querySelector('[data-ct-cn]')
  tipEl = el.querySelector('[data-ct-tip]')
}

const reduceMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

// Wipe the sheet in. Resolves once the screen is fully covered.
export function open({ title = 'Travelling…', cn = '京城', tip = true } = {}) {
  ensure()
  busy = true
  titleEl.textContent = title
  cnEl.textContent = cn
  tipEl.textContent = tip ? TIPS[Math.floor(Math.random() * TIPS.length)] : ''
  setProgress(0)
  gsap.killTweensOf([el, bar])
  el.style.display = 'flex'
  el.classList.add('travelling')
  if (reduceMotion()) {
    gsap.set(el, { clipPath: 'inset(0 0 0 0)', opacity: 1 })
    return Promise.resolve()
  }
  gsap.set(el, { clipPath: 'inset(0 100% 0 0)', opacity: 1 })
  return new Promise((resolve) => {
    gsap.to(el, { clipPath: 'inset(0 0% 0 0)', duration: 0.55, ease: 'power3.inOut', onComplete: resolve })
  })
}

// 0..1 — smoothed so a fast load still reads as motion.
export function setProgress(p) {
  if (!bar) return
  gsap.to(bar, { width: `${Math.round(Math.max(0, Math.min(1, p)) * 100)}%`, duration: 0.25, ease: 'power1.out', overwrite: true })
}

// Wipe the sheet off to the right, revealing the destination.
export function close() {
  if (!el) return Promise.resolve()
  setProgress(1)
  return new Promise((resolve) => {
    const done = () => { el.style.display = 'none'; el.classList.remove('travelling'); busy = false; resolve() }
    if (reduceMotion()) { done(); return }
    gsap.to(el, { clipPath: 'inset(0 0 0 100%)', duration: 0.6, ease: 'power3.inOut', delay: 0.25, onComplete: done })
  })
}

// Something went wrong (assets missing, offline…): say so, then let go.
export function fail(message = 'The road to the Capital is closed. Try again soon.') {
  if (!el) return Promise.resolve()
  titleEl.textContent = message
  tipEl.textContent = ''
  return new Promise((resolve) => setTimeout(() => close().then(resolve), 1800))
}

export const isBusy = () => busy
